import React, { useEffect, useState } from "react";
import { apiFetchJson } from "../api";
import { t, tf, locale } from "../i18n";
import { BugIcon, ChevronRightIcon, RefreshIcon, Spinner } from "./Icons";

// Тикеты о багах от пользователей — в администрировании (только для
// администратора). Приходят из панели («Создать тикет» у книжечки
// обновлений) и из бота («Помощь» → «Создать тикет»). Свёрнуто по умолчанию,
// но число открытых видно и в заголовке; разобранный тикет закрывается, и
// его всегда можно открыть снова.
const OPEN_STORAGE_KEY = "kl_admin_tickets_open";
const FILTERS = [
  { value: "open", label: "Открытые" },
  { value: "closed", label: "Закрытые" },
  { value: "all", label: "Все" }
];

const formatTicketTime = (ms) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return "";
  return new Date(value).toLocaleString(locale(), {
    timeZone: "Europe/Berlin",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const AdminTicketsSection = ({ isPhoneView = false, onError }) => {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(OPEN_STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [filter, setFilter] = useState("open");
  const [items, setItems] = useState(null);
  const [openCount, setOpenCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(() => new Set());

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const data = await apiFetchJson(`/api/admin/support/tickets?status=${encodeURIComponent(status)}`, { timeoutMs: 20000, retry: true });
      if (data?.success) {
        setItems(Array.isArray(data.items) ? data.items : []);
        setOpenCount(Number(data.openCount) || 0);
      }
    } catch (error) {
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  // Сколько открытых — сразу, даже когда раздел свёрнут.
  useEffect(() => {
    load(filter);
    // Только при входе в раздел настроек.
  }, []);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(OPEN_STORAGE_KEY, next ? "1" : "0"); } catch { /* приватное окно */ }
      return next;
    });
  };

  const changeFilter = (value) => {
    if (value === filter) return;
    setFilter(value);
    load(value);
  };

  const setStatus = async (ticket, status) => {
    const key = String(ticket?.id || "");
    if (!key || saving.has(key)) return;
    setSaving((prev) => new Set(prev).add(key));
    try {
      const data = await apiFetchJson(`/api/admin/support/tickets/${encodeURIComponent(key)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
        timeoutMs: 20000
      });
      if (data?.success && data.item) {
        setOpenCount(Number(data.openCount) || 0);
        // Закрытый пропадает из «Открытых», открытый снова — из «Закрытых».
        setItems((prev) => (Array.isArray(prev) ? prev : [])
          .map((item) => (String(item.id) === key ? data.item : item))
          .filter((item) => filter === "all" || item.status === filter));
      }
    } catch (error) {
      onError?.(error);
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="adm-tickets" data-phone={isPhoneView ? "1" : "0"}>
      <button type="button" className="adm-tickets-toggle kl-plain-button" onClick={toggleOpen} aria-expanded={open}>
        <BugIcon size={16} />
        <span className="adm-tickets-toggle-title">
          {t("Тикеты пользователей")}
          {openCount !== null && (
            <span className="adm-tickets-toggle-sub">
              {openCount > 0 ? tf` · открытых ${openCount}` : t(" · открытых нет")}
            </span>
          )}
        </span>
        <span className="adm-tickets-toggle-action">{open ? t("Скрыть") : t("Показать")}</span>
        <ChevronRightIcon size={16} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.18s ease" }} />
      </button>

      {open && (
        <div className="adm-tickets-body">
          <div className="adm-tickets-bar">
            <div className="adm-tickets-filters" role="tablist">
              {FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="tab"
                  className="adm-tickets-filter"
                  aria-pressed={filter === item.value}
                  onClick={() => changeFilter(item.value)}
                >
                  {t(item.label)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="adm-tickets-refresh kl-plain-button"
              onClick={() => load(filter)}
              disabled={loading}
              aria-label={t("Обновить")}
              title={t("Обновить")}
            >
              {loading ? <Spinner size={14} /> : <RefreshIcon size={14} />}
            </button>
          </div>

          {items === null ? (
            <div className="adm-tickets-empty">{loading ? t("Загрузка...") : t("Не загрузилось — нажмите «Обновить»")}</div>
          ) : items.length === 0 ? (
            <div className="adm-tickets-empty">
              {filter === "open" ? t("Открытых тикетов нет") : t("Тикетов пока нет")}
            </div>
          ) : items.map((ticket) => {
            const key = String(ticket.id);
            const busy = saving.has(key);
            const tgUsername = String(ticket.telegram?.username || "").trim();
            const contextParts = [
              ticket.context?.tab ? tf`раздел: ${ticket.context.tab}` : "",
              ticket.context?.appVersion || ""
            ].filter(Boolean);
            return (
              <div key={key} className="adm-ticket" data-status={ticket.status}>
                <div className="adm-ticket-head">
                  <span className="adm-ticket-id">#{ticket.id}</span>
                  <span className="adm-ticket-user">{ticket.username || ticket.ownerId || t("Без аккаунта")}</span>
                  <span className="adm-ticket-source">{ticket.source === "telegram" ? t("из бота") : t("из панели")}</span>
                  <span className="adm-ticket-time">{formatTicketTime(ticket.createdAt)}</span>
                  <button
                    type="button"
                    className="adm-ticket-action kl-plain-button"
                    onClick={() => setStatus(ticket, ticket.status === "open" ? "closed" : "open")}
                    disabled={busy}
                  >
                    {busy ? <Spinner size={12} /> : null}
                    {ticket.status === "open" ? t("Закрыть") : t("Открыть снова")}
                  </button>
                </div>
                <div className="adm-ticket-text">{ticket.text}</div>
                {(contextParts.length > 0 || tgUsername) && (
                  <div className="adm-ticket-meta">
                    {contextParts.join(" · ")}
                    {tgUsername && (
                      <>
                        {contextParts.length > 0 ? " · " : ""}
                        <a href={`https://t.me/${encodeURIComponent(tgUsername)}`} target="_blank" rel="noopener noreferrer">@{tgUsername}</a>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminTicketsSection;
